import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Typography, Tabs, Tab, useMediaQuery, Container, Divider, Button, IconButton } from "@mui/material";
import SVG from "react-inlinesvg";
import { FloorPlans, FloorPlan } from "../ProjectInterface";
import { masterlayoutbg } from "../../../../../assets";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseIcon from "@mui/icons-material/Close";

const CLICK_HIGHLIGHT_COLOR = "#0f63a5";
const HOVER_HIGHLIGHT_COLOR = "#eaa95e";
const DEFAULT_FILL = "transparent";
const DEFAULT_STROKE = "#000";


interface FloorPlansComponentProps {
  floorplans: FloorPlans;
}
interface Tab1Item {
  label: string; // e.g. "2BHK - 1426 sq.ft"
  bhk: number;
  sft: number;
}


const CDN_URL = process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';

const resolveImageUrl = (path: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const FloorPlansNew: React.FC<FloorPlansComponentProps> = ({ floorplans }) => {
  const isMobile = useMediaQuery("(max-width:768px)");


  // Generate unique floorplan types for Tab1
  const tab1Items: Tab1Item[] = useMemo(() => {
    const uniqueTypes = new Map<string, Tab1Item>();
    floorplans.data.forEach((fp) => {
      const key = `${fp.bhk}-${fp.sft}`;
      if (!uniqueTypes.has(key)) {
        uniqueTypes.set(key, {
          label: `${fp.bhk}BHK - ${fp.sft} sq.ft`,
          bhk: fp.bhk,
          sft: fp.sft,
        });
      }
    });
    // Sort by sqft ascending
    return Array.from(uniqueTypes.values()).sort((a, b) => a.sft - b.sft);
  }, []);

  const [tab1Index, setTab1Index] = useState(0);
  const currentTab1 = tab1Items[tab1Index];

  // Helper: Get all unique facings for a BHK + SQFT
  const getFacingsFor = useCallback((bhk: number, sft: number): string[] => {
    const set = new Set<string>();
    floorplans.data.forEach(fp => {
      if (fp.bhk === bhk && fp.sft === sft) {
        set.add(fp.facing);
      }
    });
    return Array.from(set).sort();
  }, []);

  // Get available facings for selected tab1 type for Tab2
  const tab2Facings = useMemo(() => {
    if (!currentTab1) return [];
    return getFacingsFor(currentTab1.bhk, currentTab1.sft);
  }, [currentTab1, getFacingsFor]);

  const [tab2Index, setTab2Index] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  const [scrollIndex, setScrollIndex] = useState(0);

  const visibleTabs = isMobile ? 2 : 5;

  const currentFacing = tab2Facings[tab2Index] || "";

  // Ensure tab2Index is always valid if facings change
  useEffect(() => {
    if (tab2Index >= tab2Facings.length) {
      setTab2Index(0);
    }
  }, [tab2Facings, tab2Index]);

  // Get IDs to highlight = all floorplans matching tab1 + tab2
  const highlightedIds = useMemo(() => {
    return floorplans.data
      .filter(
        (fp) =>
          fp.bhk === currentTab1.bhk &&
          fp.sft === currentTab1.sft &&
          fp.facing === currentFacing
      )
      .map((fp) => fp.gid)
      .filter((gid) => !!gid); // remove empty gid
  }, [floorplans, currentTab1, currentFacing]);

  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const svgRef = useRef<HTMLDivElement | null>(null);

  // When user clicks on a <g> in SVG, find matching floorplan and set tabs accordingly
  const onGroupClick = useCallback(
    (id: string) => {
      const plan = floorplans.data.find(fp => fp.gid === id);
      if (!plan) return;

      // Find the BHK+SQFT tab index
      const idx1 = tab1Items.findIndex(
        (item) => item.bhk === plan.bhk && item.sft === plan.sft
      );

      if (idx1 !== -1) {
        setTab1Index(idx1);

        // Immediately get facings for this type
        const facings = getFacingsFor(plan.bhk, plan.sft);
        const idx2 = facings.indexOf(plan.facing);

        // Use a microtask to ensure the correct sequence
        Promise.resolve().then(() => setTab2Index(idx2 !== -1 ? idx2 : 0));
      }
    },
    [tab1Items, getFacingsFor]
  );

  // Update SVG styles for highlight and hover
  const updateSVGStyles = useCallback(() => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;

    svg.querySelectorAll("g").forEach((g) => {
      if (!g.id) return;

      const isSelected = highlightedIds.includes(g.id);
      const isHovered = hoveredId === g.id;

      g.querySelectorAll("rect, path, polygon, ellipse").forEach((shape) => {
        shape.setAttribute(
          "fill",
          isSelected ? CLICK_HIGHLIGHT_COLOR : isHovered ? HOVER_HIGHLIGHT_COLOR : DEFAULT_FILL
        );
        shape.setAttribute("stroke", DEFAULT_STROKE);
        shape.setAttribute("stroke-width", isSelected ? "3" : isHovered ? "2" : "1");
      });
    });
  }, [highlightedIds, hoveredId]);

  // Setup event listeners on SVG load
  const onSVGLoad = useCallback(() => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;

    svg.querySelectorAll("g").forEach((g) => {
      if (!g.id) return;

      g.style.cursor = "pointer";
      g.style.pointerEvents = "all";

      const onMouseEnter = () => setHoveredId(g.id);
      const onMouseLeave = () => setHoveredId(null);
      const onClick = () => onGroupClick(g.id);

      g.addEventListener("mouseenter", onMouseEnter);
      g.addEventListener("mouseleave", onMouseLeave);
      g.addEventListener("click", onClick);
    });

    updateSVGStyles();
  }, [onGroupClick, updateSVGStyles]);

  // Update SVG styles when highlight or hover changes
  useEffect(() => {
    updateSVGStyles();
  }, [highlightedIds, hoveredId, updateSVGStyles]);

  // Reset tab2 index when tab1 changes
  useEffect(() => {
    setTab2Index(0);
  }, [tab1Index]);

  // Get floorplan image for current tab1 + tab2
  const floorplanImage = useMemo(() => {
    if (!currentTab1 || !currentFacing) return "";
    const fp = floorplans.data.find(
      (p) => p.bhk === currentTab1.bhk && p.sft === currentTab1.sft && p.facing === currentFacing
    );
    return fp?.floorplanimage || "";
  }, [currentTab1, currentFacing]);

  const handleTabScroll = (
    direction: "prev" | "next"
  ) => {

    if (direction === "next") {

      if (
        scrollIndex <
        tab1Items.length - visibleTabs
      ) {
        setScrollIndex(scrollIndex + 1);
      }

    } else {

      if (scrollIndex > 0) {
        setScrollIndex(scrollIndex - 1);
      }
    }
  };


  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: isMobile ? "auto" : "auto",
        backgroundImage: isMobile
          ? `url(${masterlayoutbg})`
          : `url(${masterlayoutbg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth="xl"

      >

        <Box
          sx={{ position: "relative", color: "#fff", display: "flex", flexDirection: "column", zIndex: 2, py: 4 }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              // mt: 3,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: "600",
                color: "white",
                display: "inline-block",
                mt: 4,
                fontFamily: 'Futura,  sans-serif !important',
                letterSpacing: "2px"
              }}
            >
              FLOOR PLANS
            </Typography>
            <Box
              sx={{
                width: {
                  xs: "120px",
                  md: "180px",
                },

                height: "2px",

                background:
                  "linear-gradient(to right, transparent, #fff, transparent)",

                mx: "auto",

                my: 1,
              }}
            />

          </Box>

          {/* Outer Tabs: Floor plan types */}
          {/* Floor plan type tabs - 2 per row on mobile, horizontal scroll on desktop */}
          {/* {isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1 }}>
              {Array.from({ length: Math.ceil(tab1Items.length / 2) }).map((_, rowIdx) => (
                <Box key={rowIdx} sx={{ display: "flex", gap: 1, mb: 1, width: "100%", justifyContent: "center" }}>
                  {tab1Items.slice(rowIdx * 2, rowIdx * 2 + 2).map((item, i) => {
                    const idx = rowIdx * 2 + i;
                    return (
                      <Box
                        key={item.label}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          mx: 0.5,
                        }}
                      >
                        <Tab
                          label={
                            <Typography
                              variant="h5"
                              sx={{
                                fontSize: 12,
                                fontWeight: tab1Index === idx ? "600" : "400",
                                color: "white",
                                textTransform: "uppercase",
                              }}
                            >
                              {item.label}
                            </Typography>
                          }
                          value={idx}
                          onClick={() => setTab1Index(idx)}
                          sx={{
                            width: "100%",
                            background: tab1Index === idx
                              ? "#14658B"
                              : "none",
                            color: tab1Index === idx ? "white" : "black",
                            fontSize: tab1Index === idx ? 16 : 14,
                            fontWeight: "bold",
                            px: 1,
                            py: 0,
                            mb: 0,
                            border: "2px solid rgb(146, 146, 146)",
                            borderRadius: 0,
                            "&:hover": {
                              background: "#14658B",
                              color: "black",
                              border: "2px solid white",
                            },
                            transition: "all 0.3s ease-in-out",
                            minHeight: 35,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ borderColor: CLICK_HIGHLIGHT_COLOR, display: "flex", justifyContent: "center" }}>
              <Tabs
                value={tab1Index}
                onChange={(_, val) => setTab1Index(val)}
                TabIndicatorProps={{ sx: { display: 'none' } }}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="floor plan types"
                textColor="inherit"
              >
                {tab1Items.map((item, i) => (
                  <Tab
                    key={item.label}
                    label={
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: 14,
                          fontWeight: tab1Index === i ? "600" : "400",
                          color: "white",
                          textTransform: "uppercase",
                        }}
                      >
                        {item.label}
                      </Typography>
                    }
                    sx={{
                      background: tab1Index === i
                        ? "#14658B"
                        : "none",
                      color: tab1Index === i ? "white" : "black",
                      fontSize: tab1Index === i ? 16 : 14,
                      fontWeight: "bold",
                      px: 4,
                      py: 0.5,
                      mb: isMobile ? 0 : 2,
                      border: "2px solid rgb(146, 146, 146)",
                      borderRadius: 0,
                      "&:hover": {
                        background: "#14658B",
                        color: "black",
                      },
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          )} */}

          <Box
            sx={{
              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              width: "100%",

              mb: 2,
            }}
          >

            {/* LEFT ARROW */}

            {tab1Items.length > visibleTabs && (

              <IconButton
                onClick={() => handleTabScroll("prev")}
                sx={{
                  width: 46,

                  height: 46,

                  flexShrink: 0,

                  background: "rgba(255,255,255,0.06)",

                  border:
                    "1px solid rgba(255,255,255,0.12)",

                  color: "white",

                  backdropFilter: "blur(10px)",

                  mr: 2,

                  transition: "0.4s",

                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#30779d,silver)",
                  },
                }}
              >
                <ArrowBackIosNewRoundedIcon
                  sx={{ fontSize: 18 }}
                />
              </IconButton>
            )}

            {/* TABS */}

            <Box
              sx={{
                width:
                  tab1Items.length > visibleTabs
                    ? {
                      xs: "72%",
                      md: "84%",
                    }
                    : "auto",

                overflow: "hidden",

                position: "relative",

                display: "flex",

                justifyContent: "center",
              }}
            >

              <Box
                sx={{
                  display: "flex",

                  justifyContent: "center",

                  gap: 2,

                  transform:
                    tab1Items.length > visibleTabs
                      ? {
                        xs: `translateX(-${scrollIndex * 145
                          }px)`,

                        md: `translateX(-${scrollIndex * 175
                          }px)`,
                      }
                      : "none",

                  transition: "0.5s ease",
                }}
              >

                {tab1Items.map((item, i) => {

                  const active = tab1Index === i;

                  return (

                    <Box
                      key={item.label}
                      sx={{
                        position: "relative",

                        flexShrink: 0,
                      }}
                    >

                      <Button
                        onClick={() => setTab1Index(i)}
                        sx={{
                          minWidth: {
                            xs: "130px",
                            md: "160px",
                          },

                          height: "40px",

                          borderRadius: "50px",

                          border: active
                            ? "1px solid silver"
                            : "1px solid rgba(255,255,255,0.12)",

                          background: active
                            ? "linear-gradient(135deg,#30779d,silver)"
                            : "rgba(255,255,255,0.06)",

                          color: "white",

                          backdropFilter: "blur(10px)",

                          fontWeight: 700,

                          letterSpacing: 1,

                          whiteSpace: "nowrap",

                          transition: "0.4s",

                          boxShadow: active
                            ? "0px 10px 35px rgba(48,119,157,0.4)"
                            : "none",

                          "&:hover": {
                            background:
                              "linear-gradient(135deg,#30779d,silver)",
                          },
                        }}
                      >
                        {item.sft} SFT
                      </Button>

                      {/* ACTIVE ARROW */}

                      {active && (

                        <Box
                          sx={{
                            position: "absolute",

                            left: "50%",

                            transform:
                              "translateX(-50%)",

                            bottom: "-12px",

                            width: 0,

                            height: 0,

                            borderLeft:
                              "10px solid transparent",

                            borderRight:
                              "10px solid transparent",

                            borderTop:
                              "12px solid #30779d",
                          }}
                        />
                      )}

                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* RIGHT ARROW */}

            {tab1Items.length > visibleTabs && (

              <IconButton
                onClick={() =>
                  handleTabScroll("next")
                }
                sx={{
                  width: 46,

                  height: 46,

                  flexShrink: 0,

                  background:
                    "rgba(255,255,255,0.06)",

                  border:
                    "1px solid rgba(255,255,255,0.12)",

                  color: "white",

                  backdropFilter: "blur(10px)",

                  ml: 2,

                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#30779d,silver)",
                  },
                }}
              >
                <ArrowForwardIosRoundedIcon
                  sx={{ fontSize: 18 }}
                />
              </IconButton>
            )}

          </Box>

          {/* Inner Tabs: Facings */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
              fleWrap: "wrap",
              gap: 2,
            }}
          >

            {tab2Facings.map((facing, i) => {

              const active = tab2Index === i;

              return (

                <Button
                  key={facing}
                  onClick={() => setTab2Index(i)}
                  sx={{
                    color: "white",

                    fontSize: {
                      xs: "13px",
                      md: "15px",
                    },

                    px: 4,

                    // py: 1,

                    borderRadius: "40px",

                    background: active
                      ? "linear-gradient(135deg,#30779d,silver)"
                      : "rgba(255,255,255,0.06)",

                    border: active
                      ? "1px solid silver"
                      : "1px solid rgba(255,255,255,0.12)",

                    letterSpacing: 1,

                    fontWeight: 600,

                    backdropFilter: "blur(10px)",

                    boxShadow: active
                      ? "0px 0px 25px rgba(15,99,165,0.4)"
                      : "none",

                    transition: "0.4s",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg,#30779d,silver)",
                    },
                  }}
                >
                  {facing} Facing
                </Button>
              );
            })}
          </Box>

          {/* Content Area */}
          <Box sx={{ flex: 1, display: "flex", gap: 6, overflow: "hidden", height: "auto", flexDirection: isMobile ? "column" : "row", px: 4 }}>
            {/* Left side floorplan image */}
            {/* <Box
              sx={{
                flex: 1,
                borderRadius: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxHeight: 500,
                border: `1px solid white`,
                // backgroundColor: "white",
                 boxShadow: "0 0 15px rgba(15, 99, 165)",
               
                m: 1.5,
                // p: 2,
              }}
            >
              {floorplanImage ? (
                <img
                  src={resolveImageUrl(floorplanImage)}
                  alt={`${currentTab1.label} - ${currentFacing}`}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <Typography>No floorplan image available</Typography>
              )}
            </Box> */}

            <Box
              sx={{
                flex: 1,

                borderRadius: "30px",

                overflow: "hidden",

                background: "rgba(255,255,255,0.05)",

                border: "1px solid rgba(255,255,255,0.08)",

                backdropFilter: "blur(18px)",

                p: { xs: 2, md: "auto" },

                boxShadow:
                  "0px 5px 5px rgba(0,0,0,0.35)",

                display: "flex",

                justifyContent: "center",

                alignItems: "center",

                // m: 1.5,
              }}
            >

              {floorplanImage ? (

                <Box
                  component="img"
                  src={resolveImageUrl(floorplanImage)}
                  alt={`${currentTab1.label} - ${currentFacing}`}

                  onClick={() => setOpenModal(true)}

                  sx={{
                    width: "100%",

                    height: {
                      xs: "280px",
                      md: "350px",
                    },

                    objectFit: "contain",

                    borderRadius: "20px",

                    cursor: "pointer",

                    transition: "0.4s",

                    "&:hover": {
                      transform: "scale(1.01)",
                    },
                  }}
                />

              ) : (

                <Typography color="white">
                  No floorplan image available
                </Typography>

              )}

            </Box>

            {/* Right side SVG map */}
            {/* <Box
              sx={{
                flex: 1,
                maxHeight: isMobile ? 420 : 520,
                // borderRadius: 2,
                // p: 1,
                color: 'white',
                borderRadius: "20px",
              }}
              ref={svgRef}
            >
              <SVG
                src={resolveImageUrl(floorplans.layoutimage)}
                onLoad={() => onSVGLoad()}
                style={{ width: "100%", height: "100%", }}
              />
            </Box> */}

            <Box
              sx={{
                flex: 1,

                maxHeight: isMobile ? 420 : 350,

                borderRadius: "30px",

                overflow: "hidden",

                background: "rgba(255,255,255,0.05)",

                border: "1px solid rgba(255,255,255,0.08)",

                backdropFilter: "blur(18px)",

                p: { xs: 2, md: 2 },

                boxShadow:
                  "0px 5px 5px rgba(0,0,0,0.35)",

                color: "white",

                display: "flex",

                justifyContent: "center",

                alignItems: "center",

                // m: 1.5,
              }}

              ref={svgRef}
            >

              <Box
                sx={{
                  width: "100%",

                  height: "100%",

                  borderRadius: "20px",

                  overflow: "hidden",

                  display: "flex",

                  justifyContent: "center",

                  alignItems: "center",

                  // background: "#fff",
                }}
              >

                <SVG
                  src={resolveImageUrl(
                    floorplans.layoutimage
                  )}

                  onLoad={() => onSVGLoad()}

                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />

              </Box>
            </Box>
          </Box>

          {/* Info Bar */}
          {/* <Box sx={{ p: 2, bgcolor: "#111", borderTop: `1px solid ${CLICK_HIGHLIGHT_COLOR}`, textAlign: "center" }}>
          <Typography variant="body2" color={CLICK_HIGHLIGHT_COLOR}>
            Selected: {currentTab1?.label} | Facing: {currentFacing} <br />
            Highlighted IDs: {highlightedIds.join(", ") || "None"} | Hovered ID: {hoveredId ?? "None"}
          </Typography>
        </Box> */}
        </Box>
      </Container>
    </Box>
  );
};

export default FloorPlansNew;






